import { useState } from 'react';
import { X, Download, BookOpen, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface WelcomeBannerProps {
  userName: string;
  onDismiss: () => void;
}

export function WelcomeBanner({ userName, onDismiss }: WelcomeBannerProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleDismiss = async () => {
    if (dontShowAgain) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('user_profiles')
            .update({ first_login_completed: true })
            .eq('id', user.id);
        }
      } catch (error) {
        console.error('Error updating first login flag:', error);
      }
    }

    setIsClosing(true);
    setTimeout(() => {
      onDismiss();
    }, 300);
  };

  const downloadPlugin = () => {
    const link = document.createElement('a');
    link.href = 'https://ptfvgyrfluuxgnjnlsps.supabase.co/storage/v1/object/public/plugin-releases/resume-doctor-ai-v6.0.9.zip';
    link.download = 'resume-doctor-ai-v6.0.9.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className={`bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg shadow-md p-6 mb-6 transition-all duration-300 ${
        isClosing ? 'opacity-0 transform -translate-y-4' : 'opacity-100'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-6 h-6 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">
              Welcome to your dashboard, {userName}!
            </h3>
          </div>
          <p className="text-gray-700 mb-4">
            Get started with Resume Doctor AI by downloading the plugin and exploring our setup guide.
          </p>
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              onClick={downloadPlugin}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download Plugin
            </button>
            <a
              href="/wordpress-plugin"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              View Setup Guide
            </a>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Don't show this message again
          </label>
        </div>
        <button
          onClick={handleDismiss}
          className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
